import { create } from 'zustand';
import { apiFetch } from '@/lib/api';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status?: string;
  plan_key?: string;
  created_at?: string;
  trial_ends_at?: string;
  onboarding_completed?: boolean;
  onboarding_completed_at?: string | null;
}

type OrgState = {
  orgs: Organization[];
  activeOrgId: string | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
};

type OrgActions = {
  fetchOrgs: () => Promise<void>;
  setActiveOrgId: (orgId: string) => void;
};

const getStoredActive = (): string | null => {
  try {
    return localStorage.getItem('ima_active_org_id');
  } catch {
    return null;
  }
};

const setStoredActive = (orgId: string | null) => {
  try {
    if (orgId) localStorage.setItem('ima_active_org_id', orgId);
    else localStorage.removeItem('ima_active_org_id');
  } catch {}
};

export const useOrgStore = create<OrgState & OrgActions>((set, get) => ({
  orgs: [],
  activeOrgId: getStoredActive(),
  isLoading: false,
  error: null,
  initialized: false,

  fetchOrgs: async () => {
    try {
      set({ isLoading: true, error: null });
      const res = await apiFetch('/api/orgs');
      if (!res.ok) throw new Error('Failed to fetch organizations');
      const payload = await res.json();
      const orgs: Organization[] = payload?.data || [];
      let active = get().activeOrgId;
      if (!active || !orgs.some(o => o.id === active)) {
        active = orgs[0]?.id || null;
        setStoredActive(active);
      }
      set({ orgs, activeOrgId: active, isLoading: false, initialized: true });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch orgs', isLoading: false, initialized: true });
    }
  },

  setActiveOrgId: (orgId: string) => {
    setStoredActive(orgId);
    set({ activeOrgId: orgId });
  },
}));

export const getActiveOrgId = (): string | null => {
  try {
    return useOrgStore.getState().activeOrgId || getStoredActive();
  } catch {
    return getStoredActive();
  }
};


